import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sparkles, Send, Loader2, Lock } from "lucide-react";
import { t } from "@/lib/i18n";

export default function AiAssistant() {
  const { user, activeCompanyId } = useAuth();
  const navigate = useNavigate();
  const isCompany = user?.role === "company";
  const cid = isCompany ? user?.company_id : activeCompanyId;
  const isPremium = ["pro", "premium"].includes(user?.subscription?.plan);

  const [messages, setMessages] = useState([
    { id: "welcome", role: "ai", text: t("Ciao! Sono l'assistente AI di ORDEXA. Chiedimi come gestire ordini, prodotti, clienti o come leggere le statistiche.") },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const endRef = useRef();

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setMessages((m) => [...m, { id: crypto.randomUUID(), role: "user", text }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/assist", { message: text, company_id: cid });
      setMessages((m) => [...m, { id: crypto.randomUUID(), role: "ai", text: data.reply }]);
    } catch (e) {
      setMessages((m) => [...m, { id: crypto.randomUUID(), role: "ai", text: apiError(e.response?.data?.detail) }]);
    } finally {
      setBusy(false);
    }
  };

  if (isCompany && !isPremium) {
    return (
      <div className="max-w-xl mx-auto text-center py-16">
        <div className="h-14 w-14 rounded-2xl bg-emerald-100 grid place-items-center mx-auto mb-5">
          <Lock className="h-7 w-7 text-emerald-600" />
        </div>
        <h1 className="font-display text-2xl font-extrabold">{t("Assistente AI — piani Pro e Premium")}</h1>
        <p className="text-muted-foreground mt-2">{t("Passa al piano Pro o Premium per avere l'assistente AI che ti aiuta a vendere di più.")}</p>
        <Button className="mt-6 font-semibold bg-emerald-600 hover:bg-emerald-700" onClick={() => navigate("/billing")} data-testid="ai-upsell-btn">
          {t("Scopri i piani")}
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto flex flex-col h-[calc(100vh-180px)]">
      <div className="flex items-center gap-2 mb-4">
        <div className="h-9 w-9 rounded-lg bg-emerald-100 grid place-items-center"><Sparkles className="h-5 w-5 text-emerald-600" /></div>
        <div>
          <h1 className="font-display text-2xl font-extrabold leading-none">{t("Assistente AI")}</h1>
          <p className="text-xs text-muted-foreground mt-1">{t("Incluso nei piani Pro e Premium")}</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto space-y-3 bg-white border border-border rounded-2xl p-4" data-testid="ai-chat-window">
        {messages.map((m) => (
          <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-primary text-primary-foreground" : "bg-secondary text-foreground"}`}>
              {m.text}
            </div>
          </div>
        ))}
        {busy && <div className="flex justify-start"><div className="bg-secondary rounded-2xl px-4 py-2.5"><Loader2 className="h-4 w-4 animate-spin" /></div></div>}
        <div ref={endRef} />
      </div>

      <div className="flex gap-2 mt-3">
        <Input value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder={t("Scrivi un messaggio…")} data-testid="ai-input" />
        <Button onClick={send} disabled={busy} className="gap-2 font-semibold" data-testid="ai-send-btn"><Send className="h-4 w-4" /></Button>
      </div>
    </div>
  );
}
